import { useState, useEffect } from 'react'
import { Users, Loader2, Clock, X } from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { joinRequestService, type JoinRequest } from '@/services/joinRequestService'

interface VisitorBannerProps {
  type: 'group' | 'league'
  groupId?: string
  isPublicGroup?: boolean
  allowJoinRequests?: boolean
}

export function VisitorBanner({ type, groupId, isPublicGroup = false, allowJoinRequests = true }: VisitorBannerProps) {
  const { isAuthenticated } = useAuth0()
  const [joinRequest, setJoinRequest] = useState<JoinRequest | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Fetch user's existing join request
  useEffect(() => {
    if (!isAuthenticated || !groupId || !isPublicGroup) return
    
    setLoading(true)
    joinRequestService.getMyRequest(groupId)
      .then(setJoinRequest)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [isAuthenticated, groupId, isPublicGroup])

  const handleRequestToJoin = async () => {
    if (!groupId) return
    
    setSubmitting(true)
    setError(null)
    try {
      const request = await joinRequestService.create(groupId)
      setJoinRequest(request)
    } catch (err) {
      setError((err as Error).message || 'Failed to submit request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelRequest = async () => {
    if (!groupId) return
    
    setSubmitting(true)
    setError(null)
    try {
      await joinRequestService.cancelMyRequest(groupId)
      setJoinRequest(null)
    } catch (err) {
      setError((err as Error).message || 'Failed to cancel request')
    } finally {
      setSubmitting(false)
    }
  }
  
  const message = type === 'group' 
    ? 'You\'re viewing this group as a visitor'
    : 'You\'re viewing this league as a visitor'
    
  const getSubMessage = () => {
    if (!isAuthenticated) {
      return type === 'group'
        ? "Sign in to join this group or access member features."
        : "Sign in to join this group or record games."
    }
    
    if (joinRequest?.status === 'pending') {
      return "Your join request is pending approval."
    }
    
    if (joinRequest?.status === 'rejected') {
      return "Your join request was not approved."
    }
    
    if (!allowJoinRequests) {
      return "This group is not accepting join requests."
    }
    
    return "You're not a member of this group yet."
  }

  const renderActionButton = () => {
    if (!isAuthenticated || !groupId || !isPublicGroup) return null
    if (loading) return <Loader2 className="h-4 w-4 animate-spin" />
    
    // Don't show join button if join requests are disabled
    if (!allowJoinRequests && !joinRequest) return null
    
    if (joinRequest?.status === 'pending') {
      return (
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleCancelRequest}
          disabled={submitting}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <X className="h-4 w-4 mr-1" />
              Cancel Request
            </>
          )}
        </Button>
      )
    }
    
    if (joinRequest?.status === 'rejected') {
      return (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <X className="h-3 w-3" />
          Request declined
        </span>
      )
    }
    
    return (
      <Button 
        size="sm"
        onClick={handleRequestToJoin}
        disabled={submitting}
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Users className="h-4 w-4 mr-1" />
            Request to Join
          </>
        )}
      </Button>
    )
  }

  return (
    <div className="border-b bg-muted/30">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
              joinRequest?.status === 'pending' 
                ? 'bg-amber-500/10' 
                : 'bg-primary/10'
            }`}>
              {joinRequest?.status === 'pending' ? (
                <Clock className="h-4 w-4 text-amber-600" />
              ) : (
                <Users className="h-4 w-4 text-primary" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium">{message}</p>
              <p className="text-xs text-muted-foreground">{getSubMessage()}</p>
              {error && <p className="text-xs text-destructive mt-0.5">{error}</p>}
            </div>
          </div>
          {renderActionButton()}
        </div>
      </div>
    </div>
  )
}
